// stores/useDateStore.ts

import { reactive, ref } from 'vue';
// import { router } from '@inertiajs/vue3'
import axios, { isAxiosError } from 'axios';

// export type BookingStoreActions = ReturnType<typeof useBookingStore>;
export type BookingStore = ReturnType<typeof useBookingStore>;

import type { AppProps, Booking, Passenger } from '@/types/appTypes';

export const useBookingStore = () => {
    // const bookingsRange = ref<AppProps['bookingsRange']>({});  // this is original. Chaged to below trying to add Passenger updates to this store.
    const bookingsRange = ref<Record<string, Booking[] | undefined>>({});

    // NEW: Generic helper for updating bookings in nested structure
    /**
     * This is updating the local Vue state ONLY. It does NOT make any server calls.
     * Call this after doing the server update.
     *
     * @param bookingId
     * @param updatedBooking
     * @returns boolean
     */
    function updateBookingById(bookingId: number, updatedBooking: Booking): boolean {
        for (const dateStr in bookingsRange.value) {
            const dateBookings = bookingsRange.value[dateStr];
            if (Array.isArray(dateBookings)) {
                const index = dateBookings.findIndex((b) => b.id === bookingId);
                if (index !== -1) {
                    // ✅ THIS LINE - replaces your splice
                    // dateBookings[index] = { ...updatedBooking };  // Shallow clone causing issues.
                    dateBookings[index] = {
                        ...updatedBooking,
                        passengers: updatedBooking.passengers ? [...updatedBooking.passengers] : [], // ✅ Deep clone array
                    };
                    return true;
                }
            }
        }
        return false;
    }

    /**
     * Get a single booking by its ID
     * @param bookingId
     * @returns Booking or null
     */
    const getBooking = (bookingId: number | null): Booking | null => {
        if (bookingId === null) {
            return null;
        }
        for (const dateStr in bookingsRange.value) {
            const dateBookings = bookingsRange.value[dateStr];
            if (Array.isArray(dateBookings)) {
                for (const booking of dateBookings) {
                    if (booking.id === bookingId) {
                        return booking;
                    }
                }
            }
        }
        return null;
    };

    /**------------------------------------------------------------------
     * Set a Booking to Completed or Uncompleted by its ID
     ------------------------------------------------------------------*/
    async function bookingCompleted(bookingId: number, setCompleted: boolean = true): Promise<boolean> {
        const booking = getBooking(bookingId);
        if (!booking) {
            console.warn('Booking not found at start of bookingCompleted');
            return false;
        }

        // Set the completed_at timestamp based on setCompleted
        if (setCompleted) {
            booking.completed_at = new Date().toISOString();
        } else {
            booking.completed_at = null;
        }
        saveBooking(booking); // This will handle the optimistic update and server call
        return true;
    }

    /**
     * Get bookings for a given date string (YYYY-MM-DD)
     * @param dateStr
     * @returns array of bookings
     */
    const getBookingsForDate = (dateStr: string): Booking[] => {
        const dateBookings = bookingsRange.value[dateStr];
        // return Array.isArray(dateBookings) ? dateBookings : [];
        return Array.isArray(dateBookings)
            ? dateBookings.filter((b) => b.flight_date === dateStr) // ← Add this line
            : [];
    };

    /**
     * Get bookings for a given date string (YYYY-MM-DD) and slot number
     * Doesn't include deleted bookings or unscheduled Bookings.
     * @param dateStr
     * @param slotNr
     * @returns array of bookings
     */
    const getBookingsForDateAndSlot = (dateStr: string, slotNr: number): Booking[] => {
        const bookingsForDate = getBookingsForDate(dateStr);
        return bookingsForDate.filter((booking) => booking.slot_nr === slotNr && booking.deleted_at === null && booking.unscheduled !== true);
    };

    /**
     * Get "Unscheduled" bookings for a given date string (YYYY-MM-DD)
     * @param dateStr
     * @param slotNr
     * @returns array of bookings
     */
    const getUnscheduledBookingsForDate = (dateStr: string): Booking[] => {
        const bookingsForDate = getBookingsForDate(dateStr);
        return bookingsForDate.filter((booking) => booking.deleted_at === null && booking.unscheduled === true);
    };

    /**
     * Get "Deleted" bookings for a given date string (YYYY-MM-DD)
     * @param dateStr
     * @param slotNr
     * @returns array of bookings
     */
    const getDeletedBookingsForDate = (dateStr: string): Booking[] => {
        const bookingsForDate = getBookingsForDate(dateStr);
        return bookingsForDate.filter((booking) => booking.deleted_at !== null);

        // ✅ Just return the array - no filtering needed
        // return Array.isArray(bookingsForDate) ? bookingsForDate : [];
    };

    /**
     * Count total passengers for a given date string (YYYY-MM-DD) and slot number
     * @param dateStr
     * @param slotNr
     * @returns number of passengers
     */
    function getTotalPassengersForDateAndSlot(dateStr: string, slotNr: number): number {
        const bookings = getBookingsForDateAndSlot(dateStr, slotNr);
        let totalPassengers = 0;
        bookings.forEach((booking) => {
            if (Array.isArray(booking.passengers)) {
                totalPassengers += booking.passengers.length;
            }
        });
        return totalPassengers;
    }

    /*********************************************************************
     * Soft Delete/Undelete a booking by its ID
     * @param bookingId
     * @param setDeleted defaults to true
     * @returns boolean success
     *********************************************************************/
    async function softDeleteBooking(bookingId: number, setDeleted: boolean = true): Promise<boolean> {
        const booking = getBooking(bookingId);
        if (!booking) {
            console.warn('Booking not found at start of softDeleteBooking');
            return false;
        }

        // 1) Optimistic FIRST
        const originalBooking = { ...booking };
        const updatedBooking: Booking = {
            ...booking,
            deleted_at: setDeleted ? new Date().toISOString() : null,
        };

        // 2) Apply optimistic update
        const optimisticSuccess = updateBookingById(bookingId, updatedBooking);
        if (!optimisticSuccess) {
            console.warn('Failed optimistic update softDeleteBooking:', bookingId);
            return false;
        }

        // 3) Then try server update
        try {
            await axios.patch(`/booking/${bookingId}/soft-delete`, { setDeleted });
            return true;
        } catch (error: unknown) {
            // 4) Always revert on ANY server error
            const revertSuccess = updateBookingById(bookingId, originalBooking);
            if (!revertSuccess) {
                console.error('Failed to revert optimistic update softDeleteBooking:', bookingId);
            }

            if (isAxiosError(error)) {
                console.error('softDeleteBooking failed isAxiosError:', error.response?.data?.error || error.message);
            }
            return false;
        }
    }

    /*********************************************************************
     * Soft Delete/Undelete a booking by its ID
     * @param bookingId
     * @param setDeleted defaults to true
     * @returns boolean success
     *********************************************************************/
    async function hardDeleteBooking(bookingId: number): Promise<boolean> {
        const booking = getBooking(bookingId);
        if (!booking) {
            console.warn('Booking not found at start of hardDeleteBooking');
            return false;
        }

        // 1) Optimistic remove from store
        for (const dateStr in bookingsRange.value) {
            const dateBookings = bookingsRange.value[dateStr];
            if (Array.isArray(dateBookings)) {
                const index = dateBookings.findIndex((b) => b.id === bookingId);
                if (index !== -1) {
                    //Delete from store. We will re-add if server call fails.
                    dateBookings.splice(index, 1);
                    break;
                }
            }
        }

        // 2) Then try server update
        try {
            await axios.delete(`/booking/${bookingId}/hard-delete`);
            return true;
        } catch (error: unknown) {
            // 3) Always revert on ANY server error. Re-add to the store.
            for (const dateStr in bookingsRange.value) {
                const dateBookings = bookingsRange.value[dateStr];
                if (Array.isArray(dateBookings)) {
                    const index = dateBookings.findIndex((b) => b.id === bookingId);
                    if (index === -1) {
                        dateBookings.push(booking);
                        break;
                    }
                }
            }

            if (isAxiosError(error)) {
                console.error('hardDeleteBooking failed isAxiosError:', error.response?.data?.error || error.message);
            }
            return false;
        }
    }

    /*********************************************************************
     * Un/Reschedule a booking by its ID
     * @param bookingId
     * @param setUnschedule defaults to true
     * @returns boolean success
     *********************************************************************/
    async function unscheduleBooking(bookingId: number, setUnscheduled: boolean = true): Promise<boolean> {
        const booking = getBooking(bookingId);
        if (!booking) {
            console.warn('Booking not found at start of unscheduleBooking');
            return false;
        }

        // 1) Optimistic FIRST
        const originalBooking = { ...booking };
        const updatedBooking: Booking = {
            ...booking,
            unscheduled: setUnscheduled,
        };

        // 2) Apply optimistic update
        const optimisticSuccess = updateBookingById(bookingId, updatedBooking);
        if (!optimisticSuccess) {
            console.warn('Failed optimistic update unscheduleBooking:', bookingId);
            return false;
        }

        // 3) Then try server update
        try {
            await axios.patch(`/booking/${bookingId}/unschedule`, { setUnscheduled });
            return true;
        } catch (error: unknown) {
            // 4) Always revert on ANY server error
            const revertSuccess = updateBookingById(bookingId, originalBooking);
            if (!revertSuccess) {
                console.error('Failed to revert optimistic update unscheduleBooking:', bookingId);
            }

            if (isAxiosError(error)) {
                console.error('unscheduleBooking failed:', error.response?.data?.error || error.message);
            }
            return false;
        }
    }

    /*********************************************************************
     * Change a Booking's slot number
     * @param bookingId
     * @param newSlotNr
     * @returns boolean success
     *********************************************************************/
    async function changeBookingSlot(bookingId: number, newSlotNr: number): Promise<boolean> {
        const booking = getBooking(bookingId);
        if (!booking) {
            console.warn('Booking not found at start of changeBookingSlot');
            return false;
        }

        // 1) Optimistic FIRST
        const originalBooking = { ...booking };
        const updatedBooking: Booking = {
            ...booking,
            slot_nr: newSlotNr,
        };

        // 2) Apply optimistic update
        const optimisticSuccess = updateBookingById(bookingId, updatedBooking);
        if (!optimisticSuccess) {
            console.warn('Failed optimistic update changeBookingSlot:', bookingId);
            return false;
        }

        // 3) Then try server update
        try {
            await axios.patch(`/booking/${bookingId}/change-slot`, { newSlotNr });
            return true;
        } catch (error: unknown) {
            // 4) Always revert on ANY server error
            const revertSuccess = updateBookingById(bookingId, originalBooking);
            if (!revertSuccess) {
                console.error('Failed to revert optimistic update changeBookingSlot:', bookingId);
            }

            if (isAxiosError(error)) {
                console.error('changeBookingSlot failed:', error.response?.data?.error || error.message);
            }
            return false;
        }
    }

    /*********************************************************************
     * Change a Booking's date.
     * Makes use of the renderKey.value++ to force WeekCal re-render
     * @param bookingId
     * @param newDateStr
     * @returns boolean success
     *********************************************************************/
    async function changeBookingDate(bookingId: number, newDateStr: string): Promise<boolean> {
        const booking = getBooking(bookingId);
        if (!booking) {
            console.warn('Booking not found at start of changeBookingDate');
            return false;
        }

        // 1) Optimistic FIRST
        const originalBooking = { ...booking };
        const updatedBooking: Booking = {
            ...booking,
            flight_date: newDateStr,
        };

        // 2) Apply optimistic update
        const optimisticSuccess = updateBookingById(bookingId, updatedBooking);
        if (!optimisticSuccess) {
            console.warn('Failed optimistic update changeBookingDate:', bookingId);
            return false;
        }

        // Optimistic: Ensure new date array exists in bookingsRange
        if (!Array.isArray(bookingsRange.value[newDateStr])) {
            bookingsRange.value[newDateStr] = []; // should never get called.
        }

        // // Move: splice from old → push to new
        // for (const dateStr in bookingsRange.value) {
        //     const dateBookings = bookingsRange.value[dateStr];
        //     if (Array.isArray(dateBookings)) {
        //         const index = dateBookings.findIndex((b) => b.id === bookingId);
        //         if (index !== -1) {
        //             const movedBooking = { ...booking, flight_date: newDateStr };
        //             dateBookings.splice(index, 1); // Remove from old
        //             bookingsRange.value[newDateStr].push(movedBooking); // Add to new
        //             break;
        //         }
        //     }
        // }

        // Find and move booking from old date → new date
        for (const dateStr in bookingsRange.value) {
            const dateBookings = bookingsRange.value[dateStr];
            if (Array.isArray(dateBookings)) {
                const index = dateBookings.findIndex((b) => b.id === bookingId);
                if (index !== -1) {
                    // Remove from old date, add to new date with updated flight_date
                    const movedBooking = { ...booking, flight_date: newDateStr };
                    dateBookings.splice(index, 1); // Triggers old date reactivity
                    bookingsRange.value[newDateStr].push(movedBooking); // Triggers new date reactivity
                    break;
                }
            }
        }

        // 3) Then try server update
        try {
            await axios.patch(`/booking/${bookingId}/change-date`, { newDateStr });
            return true;
        } catch (error: unknown) {
            // 4) Always revert on ANY server error
            const revertSuccess = updateBookingById(bookingId, originalBooking);
            if (!revertSuccess) {
                console.error('Failed to revert optimistic update changeBookingDate:', bookingId);
            }

            if (isAxiosError(error)) {
                console.error('changeBookingDate failed:', error.response?.data?.error || error.message);
            }

            return false;
        }
    }

    /**=========================================================================================================
     * Save a booking (create or update).
     * A new empty booking wil have an id of null.
     * @param booking
     * @returns boolean success
     ==========================================================================================================*/
    async function saveBooking(booking: Booking): Promise<boolean> {
        // 1) ===== Validate inputs first =====
        const dateStr = booking.flight_date;
        if (!dateStr) {
            console.warn('Invalid flight_date in booking passed to saveBooking');
            return false;
        }

        const dateRegex = /^\d{4}-\d{2}-\d{2}$/;
        if (!dateRegex.test(dateStr)) {
            console.warn('flight_date must be in YYYY-MM-DD format in booking passed to saveBooking');
            return false;
        }

        // Ensure reactive array exists
        if (!Array.isArray(bookingsRange.value[dateStr])) {
            bookingsRange.value[dateStr] = [];
        }

        // 2) ===== OPTIMISTIC UPDATE =====
        // Capture original BEFORE optimistic update + TS safe
        let isNewBooking = false;
        let optimisticId: number;
        let originalBooking: Booking | null = null;

        if (booking.id === null) {
            isNewBooking = true;
            optimisticId = Date.now() * -1;
            booking.id = optimisticId;
            bookingsRange.value[dateStr].push(booking);
        } else {
            isNewBooking = false;
            optimisticId = booking.id;
            const bookingFromStore = getBooking(booking.id);
            if (bookingFromStore) {
                originalBooking = { ...bookingFromStore };
            }
            const updateSuccess = updateBookingById(booking.id, booking);
            if (!updateSuccess) {
                console.warn('Failed optimistic update in store saveBooking for booking ID:', booking.id);
                return false;
            }
        }

        // ===== SERVER CALL =====
        try {
            // Server call
            let serverBooking: Booking;
            if (isNewBooking === true) {
                // ===== New Booking (POST) =====
                const response = await axios.post('/booking', booking);
                // const serverMessage = response.data.message || 'No message returned...';
                const newBookingId = response.data.booking?.id;
                if (!newBookingId) {
                    throw new Error('No booking ID returned from server');
                }
                // console.log('Server response message:', serverMessage);
                // console.log('New booking ID from server:', newBookingId);
                // serverBooking = { ...booking, id: newBookingId }; // update the id.
                // ✅ FIX: Use FULL server response (booking + passengers)
                serverBooking = response.data.booking; // Contains booking + passengers array!
            } else {
                // ===== UPDATE Booking (PATCH) =====
                const response = await axios.patch(`/booking/${booking.id}`, booking);
                serverBooking = response.data.booking;
            }

            // console.log('🚀 SERVER DATA:', serverBooking);
            // console.log('🟢 STORE AFTER UPDATE:', getBooking(optimisticId));

            // Success: Replace with server data (ID + any server changes)
            return updateBookingById(optimisticId, serverBooking);
        } catch (error: unknown) {
            // ===== ALWAYS REVERT optimistic changes =====
            if (isNewBooking === true) {
                // Remove new booking by temp ID
                const dayBookings = bookingsRange.value[dateStr];
                const idx = dayBookings.findIndex((b) => b.id === optimisticId);
                if (idx !== -1) {
                    dayBookings.splice(idx, 1); // delete from store.
                }
            } else if (originalBooking) {
                // Revert to original booking (when updating existing booking)
                updateBookingById(booking.id, originalBooking);
            }

            if (isAxiosError(error)) {
                console.error('saveBooking failed:', error.response?.data?.error || error.message);
                // TODO: User notification (toast)
            }
            return false;
        }
    }

    /**
     * This gets called from initStores syncStoresFromProps()
     * In theory it should always get the latest data required for this store
     * whenever the usePage().props changes on navigation.
     * @param props
     */
    const _updateFromProps = (props: AppProps) => {
        // CHANGE: Shallow clone prevents external mutations. Retains reactivity
        // through the entire bookingsRange object.
        bookingsRange.value = { ...props.bookingsRange };
    };

    return {
        bookingsRange,
        // Getters.
        getBooking, // by ID
        getBookingsForDate,
        getBookingsForDateAndSlot,
        getUnscheduledBookingsForDate, // unscheduled bookings, but not deleted
        getDeletedBookingsForDate, // deleted bookings only
        getTotalPassengersForDateAndSlot,
        // CRUD Actions
        saveBooking,
        updateBookingById,
        //
        bookingCompleted,
        // TODO: All of the below probably could be replaced by saveBooking.
        // TODO: Can just rewire existing calls to see if all works before simplifying.
        softDeleteBooking,
        hardDeleteBooking, // Permanent. Will think of making a deleted table in the db.
        unscheduleBooking,
        changeBookingSlot,
        changeBookingDate,
        // Creation Helpers
        createNewBlankBooking,
        createNewBlankPassenger,
        // Updater from init stores
        updateFromProps: _updateFromProps,
    };

    /**
     * ================================================
     * Helper Functions
     * ================================================
     */

    /**
     * ================================================
     * Create a New Blank Booking
     * ================================================
     */
    /**
     *
     * @param dateStr string        YYYY-MM-DD
     * @param slotNr number         1 to MaxSlots
     * @param nrPassengers number   Defaults to 1.
     * @returns Booking
     */
    function createNewBlankBooking(dateStr: string, slotNr: number, nrPassengers: number = 1): Booking {
        // check for valid dateStr
        if (!dateStr) {
            throw new Error('Invalid dateStr provided to createNewBlankBooking');
        }

        // dateStr Must be in YYYY-MM-DD format
        const dateRegex = /^\d{4}-\d{2}-\d{2}$/;
        if (!dateRegex.test(dateStr)) {
            throw new Error('dateStr must be in YYYY-MM-DD format in createNewBlankBooking');
        }

        // TODO: SlotNr can be 1 to MaxSlots

        // Create blank passengers
        const passengers: Passenger[] = [];
        for (let i = 0; i < nrPassengers; i++) {
            passengers.push(createNewBlankPassenger());
        }

        return {
            id: null, // null until saved
            type: 1, // Manual
            company: null,
            flight_id: null,
            flight_date: dateStr,
            photo_package: false,
            slot_nr: slotNr,
            timehint: null,
            booking_email: null,
            booking_phone: null,
            meeting_id: null,
            custom_meeting: false,
            meeting_extra: null,
            passengers: passengers,
            arrive_date: null,
            depart_date: null,
            notes: [],
            unscheduled: false,
            completed_at: null,
            created_at: null,
            updated_at: null,
            deleted_at: null,
        };
    }
    // Create a blank Passenger
    function createNewBlankPassenger(): Passenger {
        return reactive({
            id: null,
            booking_id: null,
            first: null,
            last: null,
            sex: null,
            age: null,
            fitness: null,
            weight: null,
            notes: null,
            markedForDelete: false,
            was_flown: null,
            flight_flown_id: null,
            photos_taken: null,
            flight_pilot_id: null,
            flight_paid: false,
            photos_paid: false,
            created_at: null,
            updated_at: null,
            deleted_at: null,
        });
    }
};
